import { useEffect } from 'react';

/**
 * Drives the CSS variables the global header reads to push itself below the
 * site-wide top banner:
 *
 * - `--ad-top-banner-height` — the banner's full height. Used by the absolute
 *   hero-overlay desktop header to sit statically below the banner.
 * - `--ad-top-banner-offset` — `max(0, height - scrollY)`, so the fixed mobile
 *   header slides up to the top as the banner scrolls away.
 *
 * Both are set on `targetRef` (the layout root the header descends from) and
 * cleared when inactive, restoring the default `top: 0` behaviour. The in-flow
 * banner already pushes the rest of the page (and the relative desktop header)
 * down on its own.
 */
export const useTopBannerOffset = (
    bannerRef: React.RefObject<HTMLElement | null>,
    targetRef: React.RefObject<HTMLElement | null>,
    active: boolean,
): void => {
    useEffect(() => {
        const target = targetRef.current;
        const banner = bannerRef.current;

        const clear = () => {
            target?.style.removeProperty('--ad-top-banner-height');
            target?.style.removeProperty('--ad-top-banner-offset');
        };

        if (!active || !target || !banner) {
            clear();
            return;
        }

        let height = 0;
        let frame = 0;

        const applyOffset = () => {
            frame = 0;
            // Clamp to [0, height]: scrollY can go negative during iOS rubber-band
            // overscroll, which would otherwise push the header below the banner.
            const offset = Math.min(
                height,
                Math.max(0, height - window.scrollY),
            );
            target.style.setProperty('--ad-top-banner-offset', `${offset}px`);
        };

        const requestApply = () => {
            if (!frame) {
                frame = window.requestAnimationFrame(applyOffset);
            }
        };

        const setHeight = (next: number) => {
            height = next;
            target.style.setProperty('--ad-top-banner-height', `${height}px`);
            applyOffset();
        };

        setHeight(banner.getBoundingClientRect().height);

        const resizeObserver = new ResizeObserver(() => {
            setHeight(banner.getBoundingClientRect().height);
        });
        resizeObserver.observe(banner);
        window.addEventListener('scroll', requestApply, { passive: true });

        return () => {
            resizeObserver.disconnect();
            window.removeEventListener('scroll', requestApply);
            if (frame) {
                window.cancelAnimationFrame(frame);
            }
            clear();
        };
    }, [active, bannerRef, targetRef]);
};
